import { formatDistanceToNow } from 'date-fns';
import type { Approval } from '../../types/approvals';
import { getAgentBySlug, getAgentColor } from '../../utils/agents';
import StatusBadge from '../common/StatusBadge';

const PRIORITY_STYLES: Record<string, string> = {
  urgent: 'bg-error/15 text-error',
  high: 'bg-warning/15 text-warning',
  medium: 'bg-info/15 text-info',
  low: 'bg-bg-tertiary text-text-muted',
};

const STATUS_MAP: Record<string, 'success' | 'warning' | 'error' | 'info' | 'running'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  revision_requested: 'info',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  revision_requested: 'Revision',
};

interface ApprovalCardProps {
  approval: Approval;
  onClick: () => void;
}

export default function ApprovalCard({ approval, onClick }: ApprovalCardProps) {
  const submitter = getAgentBySlug(approval.submitted_by);
  const approver = getAgentBySlug(approval.approver);
  const priorityStyle = PRIORITY_STYLES[approval.priority] || PRIORITY_STYLES.medium;

  return (
    <div
      className="bg-bg-secondary border border-border-subtle rounded-lg p-3 hover:border-border-hover cursor-pointer transition-colors"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-medium text-text-primary text-sm leading-snug">{approval.title}</h4>
        <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${priorityStyle}`}>
          {approval.priority}
        </span>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <StatusBadge
          status={STATUS_MAP[approval.status] || 'info'}
          label={STATUS_LABELS[approval.status] || approval.status}
        />
      </div>

      {approval.department ? (
        <p className="text-xs text-text-muted mb-2">{approval.department}</p>
      ) : null}

      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2">
          {submitter ? (
            <div className="flex items-center gap-1.5">
              <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-medium text-white ${getAgentColor(submitter.department)}`}>
                {submitter.name.charAt(0)}
              </div>
              <span className="text-xs text-text-secondary">{submitter.shortName}</span>
            </div>
          ) : (
            <span className="text-xs text-text-muted">{approval.submitted_by}</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {approver ? (
            <span className="text-xs text-text-muted">
              {approver.shortName}
            </span>
          ) : null}
          <span className="text-xs text-text-muted">
            {formatDistanceToNow(new Date(approval.submitted_at), { addSuffix: true })}
          </span>
        </div>
      </div>
    </div>
  );
}
